import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { CardComp } from "../../../../components";
import TableSkeletonLoader from "../../../../components/Common/TableSkeletonLoader";
import ConfirmationModal from "../../../../components/Common/ConfirmationModal";
import InvestorPortalPageHeader, {
  InvestorPortalPrimaryButton,
} from "../../../../components/Admin/InvestorPortal/InvestorPortalPageHeader";
import InvestorPortalActionButton, {
  InvestorPortalActionStack,
} from "../../../../components/Admin/InvestorPortal/InvestorPortalActionButton";
import InvestorPortalTableEmpty from "../../../../components/Admin/InvestorPortal/InvestorPortalTableEmpty";
import MarketplaceProductImageUploader, {
  mediaToCoverImage,
} from "../../../../components/Admin/InvestorPortal/MarketplaceProductImageUploader";
import { productToApiBody, staffMarketplaceProductPath } from "../../../../utils/staffMarketplaceAdapter";
import { getProductLifecycleActions } from "../../../../store/admin/marketplace/productLifecycle";
import { PRODUCT_STATUS } from "../../../../store/admin/marketplace/constants";
import {
  useListStaffMarketplaceCategoriesQuery,
  useGetStaffMarketplaceProductQuery,
  useCreateStaffMarketplaceProductMutation,
  useUpdateStaffMarketplaceProductMutation,
  useStaffMarketplaceProductLifecycleMutation,
} from "../../../../store/admin/marketplace/staffMarketplaceApi";
import { withMarketplaceGuard } from "../withInvestorPortalGuard";

const emptyForm = {
  name: "",
  categoryId: "",
  subcategoryId: "",
  sku: "",
  shortDescription: "",
  fullDescription: "",
  sellerName: "",
  priceNgn: "",
  unitLabel: "unit",
  stockQuantity: 0,
  lowStockThreshold: 5,
  trackInventory: true,
  preOrderAllowed: false,
  minimumQuantity: 1,
  maximumQuantity: "",
  deliveryAvailable: true,
  pickupAvailable: false,
  pickupLocations: [""],
  estimatedFulfillmentTime: "",
  media: [],
  coverImage: "",
};

function productTargetToAction(target, currentStatus) {
  if (target === PRODUCT_STATUS.INTERNAL_REVIEW) return "submit-review";
  if (target === PRODUCT_STATUS.PUBLISHED) return "publish";
  if (target === PRODUCT_STATUS.UNPUBLISHED) return "unpublish";
  if (target === PRODUCT_STATUS.OUT_OF_STOCK) return "mark-out-of-stock";
  if (target === PRODUCT_STATUS.ARCHIVED) return "archive";
  if (target === PRODUCT_STATUS.DRAFT) {
    return currentStatus === PRODUCT_STATUS.INTERNAL_REVIEW ? "return-to-draft" : "unpublish";
  }
  return null;
}

function ProductFormPage() {
  const { productSlug } = useParams();
  const productKey = productSlug;
  const isEdit = Boolean(productKey);
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [confirm, setConfirm] = useState(null);
  const [saving, setSaving] = useState(false);

  const { data: categoriesData } = useListStaffMarketplaceCategoriesQuery({ per_page: 100 });
  const {
    data: product,
    isFetching,
    isError,
    refetch,
  } = useGetStaffMarketplaceProductQuery(productKey, { skip: !isEdit });
  const [createProduct] = useCreateStaffMarketplaceProductMutation();
  const [updateProduct] = useUpdateStaffMarketplaceProductMutation();
  const [lifecycleAction, { isLoading: isLifecycleLoading }] =
    useStaffMarketplaceProductLifecycleMutation();

  const allCategories = categoriesData?.items || [];
  const topLevelCategories = useMemo(() => {
    const tops = allCategories.filter((c) => !c.parentId && c.isActive !== false);
    if (
      form.categoryId &&
      !tops.some((c) => String(c.id) === String(form.categoryId))
    ) {
      const current = allCategories.find((c) => String(c.id) === String(form.categoryId));
      if (current && !current.parentId) tops.push(current);
    }
    return tops;
  }, [allCategories, form.categoryId]);

  const subcategoryOptions = useMemo(() => {
    const subs = allCategories.filter(
      (c) =>
        c.parentId &&
        String(c.parentId) === String(form.categoryId) &&
        c.isActive !== false
    );
    if (
      form.subcategoryId &&
      !subs.some((c) => String(c.id) === String(form.subcategoryId))
    ) {
      const current = allCategories.find(
        (c) => String(c.id) === String(form.subcategoryId)
      );
      if (current) subs.push(current);
    }
    return subs;
  }, [allCategories, form.categoryId, form.subcategoryId]);

  useEffect(() => {
    if (!product) return;
    const media = Array.isArray(product.media) ? product.media : [];
    const normalizedMedia =
      media.length > 0
        ? media
        : product.coverImage
          ? [{ url: product.coverImage, type: "image" }]
          : [];
    setForm({
      name: product.name || "",
      categoryId: product.categoryId || "",
      subcategoryId: product.subcategoryId || "",
      sku: product.sku || "",
      shortDescription: product.shortDescription || "",
      fullDescription: product.fullDescription || "",
      sellerName: product.sellerName || "",
      priceNgn: product.priceNgn ?? product.price ?? "",
      unitLabel: product.unitLabel || "unit",
      stockQuantity: product.stockQuantity ?? 0,
      lowStockThreshold: product.lowStockThreshold ?? 5,
      trackInventory: product.trackInventory !== false,
      preOrderAllowed: !!product.preOrderAllowed,
      minimumQuantity: product.minimumQuantity ?? 1,
      maximumQuantity: product.maximumQuantity ?? "",
      deliveryAvailable: product.deliveryAvailable !== false,
      pickupAvailable: !!product.pickupAvailable,
      pickupLocations:
        Array.isArray(product.pickupLocations) && product.pickupLocations.length
          ? product.pickupLocations
          : product.pickupLocation
            ? [product.pickupLocation]
            : [""],
      estimatedFulfillmentTime: product.estimatedFulfillmentTime || "",
      media: normalizedMedia,
      coverImage: product.coverImage || mediaToCoverImage(normalizedMedia) || "",
    });
  }, [product]);

  const lifecycleActions = useMemo(
    () => (isEdit ? getProductLifecycleActions(product?.lifecycleStatus || product?.status) : []),
    [isEdit, product]
  );

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Product name is required.");
      return;
    }
    if (!form.categoryId) {
      toast.error("Category is required.");
      return;
    }
    if (form.priceNgn === "" || Number(form.priceNgn) < 0) {
      toast.error("Enter a valid price.");
      return;
    }
    if (
      form.pickupAvailable &&
      !(form.pickupLocations || []).map((l) => String(l || "").trim()).filter(Boolean).length
    ) {
      toast.error("Add at least one pickup location before enabling pickup.");
      return;
    }

    const media = (form.media || []).map((item) =>
      typeof item === "string" ? { url: item, type: "image" } : { url: item.url, type: "image", alt: item.alt || "" }
    );

    const body = productToApiBody({
      ...form,
      subcategoryId: form.subcategoryId || null,
      coverImage: mediaToCoverImage(media) || form.coverImage || null,
      media: media.length ? media : null,
      priceNgn: Number(form.priceNgn),
      stockQuantity: form.stockQuantity === "" ? null : Number(form.stockQuantity),
      lowStockThreshold: form.lowStockThreshold === "" ? null : Number(form.lowStockThreshold),
      minimumQuantity: Number(form.minimumQuantity) || 1,
      maximumQuantity: form.maximumQuantity === "" ? null : Number(form.maximumQuantity),
    });

    setSaving(true);
    try {
      if (isEdit) {
        await updateProduct({ id: productKey, body }).unwrap();
        toast.success("Product updated");
        navigate(staffMarketplaceProductPath(product?.slug || productKey));
      } else {
        const created = await createProduct(body).unwrap();
        toast.success("Product created");
        navigate(staffMarketplaceProductPath(created));
      }
    } catch (error) {
      toast.error(error?.data?.message || "Unable to save product.");
    } finally {
      setSaving(false);
    }
  };

  const runLifecycle = (actionDef) => {
    if (actionDef.action === "preview") {
      const status = product?.lifecycleStatus || product?.status;
      if (status !== PRODUCT_STATUS.PUBLISHED && status !== PRODUCT_STATUS.OUT_OF_STOCK) {
        toast.error("Publish this product before viewing the public page.");
        return;
      }
      const slug = product?.slug || productKey;
      window.open(`${window.location.origin}/invest/marketplace/${slug}`, "_blank");
      return;
    }
    const action = productTargetToAction(actionDef.target, product?.lifecycleStatus || product?.status);
    if (!action) {
      toast.error("This action is not available.");
      return;
    }
    setConfirm({
      title: actionDef.label,
      message: `Are you sure you want to ${actionDef.label.toLowerCase()}?`,
      confirmText: "Yes, continue",
      confirmButtonClass:
        actionDef.variant === "danger" ? "bg-danger text-white" : "bg-primary1 text-white",
      action: async () => {
        try {
          await lifecycleAction({ id: productKey, action }).unwrap();
          toast.success(actionDef.label);
        } catch (error) {
          toast.error(error?.data?.message || "Lifecycle action failed.");
        }
        setConfirm(null);
      },
    });
  };

  if (isEdit && isFetching && !product) {
    return (
      <div className="pb-4">
        <TableSkeletonLoader rows={8} columns={2} />
      </div>
    );
  }

  if (isEdit && (isError || !product)) {
    return (
      <div className="pb-4">
        <InvestorPortalTableEmpty
          variant="error"
          title="We couldn’t load this product."
          onCta={() => refetch()}
        />
      </div>
    );
  }

  return (
    <div className="pb-4">
      <InvestorPortalPageHeader
        title={isEdit ? "Edit product" : "New product"}
        subtitle="Basic product details for the investor marketplace."
        action={
          <InvestorPortalPrimaryButton
            btnText={saving ? "Saving..." : "Save"}
            onClick={handleSave}
            loading={saving}
          />
        }
      />

      <div className="row g-3">
        <div className="col-lg-8">
          <CardComp cardClassName="border-0 shadow-sm mb-3">
            <h6 className="fw-semibold mb-3">Basic information</h6>
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label small fw-semibold">Name</label>
                <input
                  className="form-control"
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label small fw-semibold">Category</label>
                <select
                  className="form-select"
                  value={form.categoryId}
                  onChange={(e) => {
                    setForm((prev) => ({
                      ...prev,
                      categoryId: e.target.value,
                      subcategoryId: "",
                    }));
                  }}
                >
                  <option value="">Select category</option>
                  {topLevelCategories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label small fw-semibold">Subcategory</label>
                <select
                  className="form-select"
                  value={form.subcategoryId}
                  onChange={(e) => setField("subcategoryId", e.target.value)}
                  disabled={!form.categoryId}
                >
                  <option value="">
                    {form.categoryId
                      ? subcategoryOptions.length
                        ? "Optional — select subcategory"
                        : "No subcategories for this category"
                      : "Select a category first"}
                  </option>
                  {subcategoryOptions.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label small fw-semibold">SKU</label>
                <input
                  className="form-control"
                  value={form.sku}
                  onChange={(e) => setField("sku", e.target.value)}
                />
              </div>
              <div className="col-12">
                <label className="form-label small fw-semibold">Short description</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={form.shortDescription}
                  onChange={(e) => setField("shortDescription", e.target.value)}
                />
              </div>
              <div className="col-12">
                <label className="form-label small fw-semibold">Full description</label>
                <textarea
                  className="form-control"
                  rows={5}
                  value={form.fullDescription}
                  onChange={(e) => setField("fullDescription", e.target.value)}
                />
              </div>
              <div className="col-12">
                <label className="form-label small fw-semibold">Seller name</label>
                <input
                  className="form-control"
                  value={form.sellerName}
                  onChange={(e) => setField("sellerName", e.target.value)}
                />
              </div>
            </div>
          </CardComp>

          <CardComp cardClassName="border-0 shadow-sm mb-3">
            <h6 className="fw-semibold mb-3">Product images</h6>
            <MarketplaceProductImageUploader
              media={form.media}
              onChange={(nextMedia) => {
                setForm((prev) => ({
                  ...prev,
                  media: nextMedia,
                  coverImage: mediaToCoverImage(nextMedia) || "",
                }));
              }}
            />
          </CardComp>

          <CardComp cardClassName="border-0 shadow-sm mb-3">
            <h6 className="fw-semibold mb-3">Pricing & inventory</h6>
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label small fw-semibold">Price (NGN)</label>
                <input
                  type="number"
                  className="form-control"
                  value={form.priceNgn}
                  onChange={(e) => setField("priceNgn", e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label small fw-semibold">Unit label</label>
                <input
                  className="form-control"
                  value={form.unitLabel}
                  onChange={(e) => setField("unitLabel", e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label small fw-semibold">Stock quantity</label>
                <input
                  type="number"
                  className="form-control"
                  value={form.stockQuantity}
                  onChange={(e) => setField("stockQuantity", e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label small fw-semibold">Low stock threshold</label>
                <input
                  type="number"
                  className="form-control"
                  value={form.lowStockThreshold}
                  onChange={(e) => setField("lowStockThreshold", e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label small fw-semibold">Minimum quantity</label>
                <input
                  type="number"
                  className="form-control"
                  value={form.minimumQuantity}
                  onChange={(e) => setField("minimumQuantity", e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label small fw-semibold">Maximum quantity</label>
                <input
                  type="number"
                  className="form-control"
                  value={form.maximumQuantity}
                  onChange={(e) => setField("maximumQuantity", e.target.value)}
                />
              </div>
              <div className="col-12 d-flex flex-wrap gap-4">
                <label className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={form.trackInventory}
                    onChange={(e) => setField("trackInventory", e.target.checked)}
                  />
                  <span className="form-check-label small">Track inventory</span>
                </label>
                <label className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={form.preOrderAllowed}
                    onChange={(e) => setField("preOrderAllowed", e.target.checked)}
                  />
                  <span className="form-check-label small">Allow pre-order</span>
                </label>
              </div>
            </div>
          </CardComp>

          <CardComp cardClassName="border-0 shadow-sm mb-3">
            <h6 className="fw-semibold mb-3">Delivery & pickup</h6>
            <div className="row g-3">
              <div className="col-12 d-flex flex-wrap gap-4">
                <label className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={form.deliveryAvailable}
                    onChange={(e) => setField("deliveryAvailable", e.target.checked)}
                  />
                  <span className="form-check-label small">Delivery available</span>
                </label>
                <label className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={form.pickupAvailable}
                    onChange={(e) => {
                      const enabled = e.target.checked;
                      const hasLocation = (form.pickupLocations || [])
                        .map((l) => String(l || "").trim())
                        .some(Boolean);
                      if (enabled && !hasLocation) {
                        toast.error("Enter at least one pickup location first, then enable pickup.");
                        return;
                      }
                      setField("pickupAvailable", enabled);
                    }}
                  />
                  <span className="form-check-label small">Pickup available</span>
                </label>
              </div>
              <div className="col-12">
                <label className="form-label small fw-semibold">
                  Pickup locations{form.pickupAvailable ? " *" : ""}
                </label>
                <div className="d-flex flex-column gap-2">
                  {(form.pickupLocations || [""]).map((location, index) => (
                    <div key={`pickup-${index}`} className="d-flex gap-2">
                      <input
                        className="form-control"
                        value={location}
                        onChange={(e) => {
                          const next = [...(form.pickupLocations || [""])];
                          next[index] = e.target.value;
                          setField("pickupLocations", next);
                        }}
                        placeholder={`Pickup location ${index + 1}`}
                        required={form.pickupAvailable && index === 0}
                      />
                      {(form.pickupLocations || []).length > 1 ? (
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm px-3"
                          onClick={() => {
                            const next = (form.pickupLocations || []).filter((_, i) => i !== index);
                            setField("pickupLocations", next.length ? next : [""]);
                          }}
                        >
                          Remove
                        </button>
                      ) : null}
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  className="btn btn-link btn-sm px-0 mt-2"
                  onClick={() =>
                    setField("pickupLocations", [...(form.pickupLocations || [""]), ""])
                  }
                >
                  + Add another pickup location
                </button>
                <p className="small text-muted mb-0 mt-1">
                  Customers choose one of these locations at checkout. At least one is required
                  before pickup can be offered.
                </p>
              </div>
              <div className="col-md-6">
                <label className="form-label small fw-semibold">Estimated fulfillment</label>
                <input
                  className="form-control"
                  value={form.estimatedFulfillmentTime}
                  onChange={(e) => setField("estimatedFulfillmentTime", e.target.value)}
                  placeholder="e.g. 2-3 business days"
                />
              </div>
            </div>
          </CardComp>
        </div>

        <div className="col-lg-4">
          <CardComp cardClassName="border-0 shadow-sm mb-3">
            <h6 className="fw-semibold mb-3">Actions</h6>
            <InvestorPortalActionStack>
              <InvestorPortalActionButton
                btnText="Cancel"
                variant="ghost"
                onClick={() =>
                  navigate(
                    isEdit
                      ? staffMarketplaceProductPath(product?.slug || productKey)
                      : "/super_admin/investor_portal/marketplace/products"
                  )
                }
              />
            </InvestorPortalActionStack>
          </CardComp>

          {isEdit && lifecycleActions.length > 0 ? (
            <CardComp cardClassName="border-0 shadow-sm">
              <h6 className="fw-semibold mb-3">Lifecycle</h6>
              <InvestorPortalActionStack>
                {lifecycleActions.map((action) => (
                  <InvestorPortalActionButton
                    key={action.label}
                    btnText={action.label}
                    variant={action.variant || "outline"}
                    disabled={isLifecycleLoading}
                    onClick={() => runLifecycle(action)}
                  />
                ))}
              </InvestorPortalActionStack>
            </CardComp>
          ) : null}
        </div>
      </div>

      <ConfirmationModal
        isOpen={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        onConfirm={confirm?.action}
        title={confirm?.title}
        message={confirm?.message}
        confirmText={confirm?.confirmText}
        confirmButtonClass={confirm?.confirmButtonClass}
        isLoading={isLifecycleLoading}
      />
    </div>
  );
}

export default withMarketplaceGuard(ProductFormPage);
